import { useState, useEffect } from 'react';
import { 
  Award, 
  Users, 
  TrendingUp, 
  History,
  Star
} from 'lucide-react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';

export function RankingFidelidade() {
  const [loading, setLoading] = useState(true);
  const [ranking, setRanking] = useState<any[]>([]);

  useEffect(() => {
    carregarRanking();
  }, []);

  const carregarRanking = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/fidelidade/ranking');
      setRanking(data);
    } catch (error) {
      console.error('Erro ao carregar ranking:', error);
      setRanking([]); // fallback para evitar loading infinito
    } finally {
      setLoading(false);
    }
  };

  const getNivelBadge = (nivel: string) => {
    const config: any = {
      BRONZE: { color: 'bg-orange-100 text-orange-700', label: 'Bronze' },
      PRATA: { color: 'bg-gray-100 text-gray-700', label: 'Prata' },
      OURO: { color: 'bg-yellow-100 text-yellow-700', label: 'Ouro' },
      DIAMANTE: { color: 'bg-blue-100 text-blue-700', label: 'Diamante' },
    };
    return config[nivel] || { color: 'bg-gray-100', label: nivel };
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Award className="w-7 h-7 text-yellow-500" />
            Fort3D Club
          </h1>
          <p className="text-gray-500">Gestão do programa de fidelidade e clientes VIP</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Participantes</p>
                <h3 className="text-2xl font-bold">{ranking.length}</h3>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-50 rounded-lg">
                <Star className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Pontos em Circulação</p>
                <h3 className="text-2xl font-bold">
                  {ranking.reduce((sum, r) => sum + r.pontosAtuais, 0).toLocaleString()}
                </h3>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-50 rounded-lg">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Economia Gerada (Cashback)</p>
                <h3 className="text-2xl font-bold">
                  R$ {(ranking.reduce((sum, r) => sum + r.pontosResgatados, 0) * 0.05).toFixed(2)}
                </h3>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <History className="w-5 h-5" />
            Ranking de Clientes (Top 20)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-gray-500">Carregando ranking...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 text-left">
                    <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase">Posição</th>
                    <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase">Cliente</th>
                    <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase text-center">Nível</th>
                    <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase text-right">Saldo Atual</th>
                    <th className="py-3 px-4 text-xs font-bold text-gray-500 uppercase text-right">Total Acumulado</th>
                  </tr>
                </thead>
                <tbody>
                  {ranking.map((resumo, idx) => (
                    <tr key={resumo.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-4">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                          idx === 1 ? 'bg-gray-100 text-gray-500' :
                          idx === 2 ? 'bg-orange-100 text-orange-700' : 'text-gray-400'
                        }`}>
                          {idx + 1}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <p className="font-semibold text-gray-900">{resumo.cliente.nome}</p>
                        <p className="text-xs text-gray-500">{resumo.cliente.email}</p>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <Badge className={`${getNivelBadge(resumo.nivel).color} border-none`}>
                          {getNivelBadge(resumo.nivel).label}
                        </Badge>
                      </td>
                      <td className="py-4 px-4 text-right font-bold text-yellow-600">
                        {resumo.pontosAtuais.toLocaleString()} pts
                      </td>
                      <td className="py-4 px-4 text-right text-sm text-gray-500">
                        {resumo.pontosAcumuladosTotal.toLocaleString()} pts
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
